import { useState } from 'react';

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' && typeof value === 'string') {
          params.append(key, value);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9cihaalh2lp4rjvssn0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormError('Your submission could not be processed. Please try again.');
        } else {
          setFormStatus('error');
          setFormError(serverMsg || 'Something went wrong. Please try again.');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('Network error. Please check your connection and try again.');
    }
  };

  return (
    <section id="placement" className="py-24 md:py-32 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-16 md:mb-20">
          <span className="inline-block bg-primary-100 text-primary-700 text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider label-font mb-5">
            Get In Touch
          </span>
          <h2 className="heading-font text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 max-w-3xl mx-auto leading-tight">
            Ready to make a <span className="italic text-primary-600">difference?</span>
          </h2>
          <p className="text-foreground-600 mt-5 max-w-2xl mx-auto leading-relaxed text-base md:text-lg">
            Every young person deserves a turning point. If you&apos;re a placing agency looking for a partner who builds futures, we&apos;d love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
              <h3 className="heading-font text-xl font-bold text-foreground-900 mb-5">Contact Information</h3>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-700 rounded-xl flex-shrink-0">
                    <i className="ri-phone-line"></i>
                  </span>
                  <div>
                    <p className="font-semibold text-foreground-900 text-sm label-font">Direct Line</p>
                    <a href="tel:12892046406" className="text-foreground-600 text-sm hover:text-primary-600 transition-colors">1 289 204 6406</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-700 rounded-xl flex-shrink-0">
                    <i className="ri-phone-fill"></i>
                  </span>
                  <div>
                    <p className="font-semibold text-foreground-900 text-sm label-font">Emergency Placement Line</p>
                    <a href="tel:12892046406" className="text-foreground-600 text-sm hover:text-accent-600 transition-colors">1 289 204 6406</a>
                    <p className="text-foreground-400 text-xs mt-0.5">Available 24/7 for urgent placement requests</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-secondary-100 text-secondary-700 rounded-xl flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </span>
                  <div>
                    <p className="font-semibold text-foreground-900 text-sm label-font">Email</p>
                    <a href="mailto:info@mctresidential.com" className="text-foreground-600 text-sm hover:text-primary-600 transition-colors">info@mctresidential.com</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-700 rounded-xl flex-shrink-0">
                    <i className="ri-map-pin-2-line"></i>
                  </span>
                  <div>
                    <p className="font-semibold text-foreground-900 text-sm label-font">Office Location</p>
                    <p className="text-foreground-600 text-sm">Ontario, Canada</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-700 rounded-xl flex-shrink-0">
                    <i className="ri-time-line"></i>
                  </span>
                  <div>
                    <p className="font-semibold text-foreground-900 text-sm label-font">Business Hours</p>
                    <p className="text-foreground-600 text-sm">Monday &mdash; Friday, 9:00 AM &mdash; 5:00 PM</p>
                    <p className="text-foreground-400 text-xs mt-0.5">Emergency placement line available 24/7</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-background-200/70 h-[200px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d369106.82903524695!2d-79.68632656341842!3d43.71824127352243!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89d4cb90d7c63ba5%3A0x323555502ab4c477!2sOntario!5e0!3m2!1sen!2sca!4v1690000000000!5m2!1sen!2sca"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="MCT Residential Services Office Location"
              ></iframe>
            </div>

            <a
              href="tel:12892046406"
              className="flex items-center justify-center gap-2 w-full bg-primary-500 text-white py-4 rounded-full text-base font-semibold label-font hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line"></i>
              </span>
              Call Now: 1 289 204 6406
            </a>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
              <h3 className="heading-font text-xl font-bold text-foreground-900 mb-2">
                Request Placement Information
              </h3>
              <p className="text-foreground-500 text-sm mb-6 leading-relaxed">
                Fill out the form below and we&apos;ll respond within one business day.
              </p>

              {formStatus === 'success' ? (
                <div className="text-center py-12">
                  <span className="w-16 h-16 flex items-center justify-center bg-accent-100 text-accent-600 rounded-full mx-auto mb-5">
                    <i className="ri-check-line text-2xl"></i>
                  </span>
                  <h4 className="heading-font text-xl font-bold text-foreground-900 mb-2">Thank You!</h4>
                  <p className="text-foreground-500 text-sm leading-relaxed max-w-md mx-auto">
                    We&apos;ve received your inquiry and will respond within one business day. For urgent placement requests, please call 1 289 204 6406.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5 label-font">
                        Your Name <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        className="w-full bg-background-50 border border-background-300 rounded-lg px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="agency" className="block text-sm font-medium text-foreground-700 mb-1.5 label-font">
                        Agency / Organization <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="agency"
                        name="agency"
                        type="text"
                        required
                        className="w-full bg-background-50 border border-background-300 rounded-lg px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="Your agency name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5 label-font">
                        Email Address <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        className="w-full bg-background-50 border border-background-300 rounded-lg px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="you@agency.ca"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5 label-font">
                        Phone Number
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        className="w-full bg-background-50 border border-background-300 rounded-lg px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="1 289 204 6406"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="youth_age" className="block text-sm font-medium text-foreground-700 mb-1.5 label-font">
                        Youth Age Range
                      </label>
                      <select
                        id="youth_age"
                        name="youth_age"
                        className="w-full bg-background-50 border border-background-300 rounded-lg px-4 py-3 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors cursor-pointer"
                      >
                        <option value="">Select age range</option>
                        <option value="12-14">12 &mdash; 14 years</option>
                        <option value="15-16">15 &mdash; 16 years</option>
                        <option value="17-18">17 &mdash; 18 years</option>
                        <option value="multiple">Multiple youth</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="placement_type" className="block text-sm font-medium text-foreground-700 mb-1.5 label-font">
                        Placement Type
                      </label>
                      <select
                        id="placement_type"
                        name="placement_type"
                        className="w-full bg-background-50 border border-background-300 rounded-lg px-4 py-3 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors cursor-pointer"
                      >
                        <option value="">Select type</option>
                        <option value="emergency">Emergency</option>
                        <option value="planned">Planned</option>
                        <option value="transitional">Transitional</option>
                        <option value="exploring">Just exploring options</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5 label-font">
                      Additional Details
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full bg-background-50 border border-background-300 rounded-lg px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                      placeholder="Tell us about the youth and what kind of support they need..."
                    ></textarea>
                    <p className="text-foreground-400 text-xs mt-1">Maximum 500 characters</p>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="absolute opacity-0 pointer-events-none"
                    style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '1px', height: '1px', opacity: 0, pointerEvents: 'none' }}
                  />

                  {formStatus === 'error' && formError && (
                    <p className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">{formError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full bg-primary-500 text-white py-3.5 rounded-full text-sm font-semibold label-font hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'submitting' ? 'Submitting...' : 'Send Inquiry'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}